// Shared dashboard building blocks — the card shell, its header, the KPI tile and
// the section heading — used by Home and the sections split out of it. Plain
// constants live in tileStyles.ts (this file exports components only, for Fast Refresh).

import { useState } from 'react';
import { Link } from 'react-router-dom';

export function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function CardHeader({ title, link }: { title: string; link?: { to: string; label: string } }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
      {link && (
        <Link to={link.to} style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>
          {link.label} →
        </Link>
      )}
    </div>
  );
}

/** The muted all-caps-ish heading above each dashboard section. */
export function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--fg-muted)', letterSpacing: '.02em' }}>
      {children}
    </h2>
  );
}

export function Kpi({ label, value, sub, subColor, to }: { label: string; value: string; sub?: string; subColor?: string; to?: string }) {
  // A KPI with a `to` becomes a whole-card link to its detail page, with a hover
  // lift (stronger border + hover fill). Without `to` it stays a static tile.
  const [hover, setHover] = useState(false);
  const style: React.CSSProperties = {
    display: 'block',
    background: hover ? 'var(--bg-hover)' : 'var(--bg-elev)',
    border: `1px solid ${hover ? 'var(--border-str)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)',
    padding: '12px 14px',
    transition: 'background 120ms ease, border-color 120ms ease',
  };
  const body = (
    <>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, marginTop: 2, color: subColor ?? 'var(--fg-muted)' }}>{sub}</div>}
    </>
  );
  if (!to) return <div style={style}>{body}</div>;
  return (
    <Link
      to={to}
      style={{ ...style, color: 'var(--fg)', textDecoration: 'none' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {body}
    </Link>
  );
}
